import { displayName } from '../../types';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add, Work, People } from '@mui/icons-material';
import { apiService } from '../../services/api';
import CreateRequisitionModal from '../../components/CreateRequisitionModal';
import AddCandidateModal from '../../components/AddCandidateModal';
import RequisitionDetailModal from '../../components/RequisitionDetailModal';

const Recruitment: React.FC = () => {
  const [requisitions, setRequisitions] = useState<any[]>([]);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [pipeline, setPipeline] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isRequisitionModalOpen, setIsRequisitionModalOpen] = useState(false);
  const [isCandidateModalOpen, setIsCandidateModalOpen] = useState(false);
  const [selectedRequisitionId, setSelectedRequisitionId] = useState<string | null>(null);

  const fetchRecruitmentData = async () => {
    try {
      setLoading(true);
      const [reqs, cands, pipe] = await Promise.all([
        apiService.getRequisitions(),
        apiService.getCandidates(),
        apiService.getRecruitmentPipeline(),
      ]);
      setRequisitions(reqs);
      setCandidates(cands);
      setPipeline(pipe);
    } catch (err: any) {
      console.error('Error fetching recruitment data:', err);
      setError('Failed to load recruitment data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchRecruitmentDataEffect = async () => {
      try {
        setLoading(true);
        const [reqs, cands, pipe] = await Promise.all([
          apiService.getRequisitions(),
          apiService.getCandidates(),
          apiService.getRecruitmentPipeline(),
        ]);
        setRequisitions(reqs);
        setCandidates(cands);
        setPipeline(pipe);
      } catch (err: any) {
        console.error('Error fetching recruitment data:', err);
        setError('Failed to load recruitment data');
      } finally {
        setLoading(false);
      }
    };

    fetchRecruitmentDataEffect();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'success';
      case 'pending_approval':
        return 'warning';
      case 'approved':
        return 'info';
      case 'filled':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent={{ xs: "column", sm: "space-between" }} alignItems={{ xs: "flex-start", sm: "center" }} gap={1} mb={3}>
        <Typography variant="h4">Recruitment</Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<People />} 
            sx={{ mr: 2 }}
            onClick={() => setIsCandidateModalOpen(true)}
          >
            Add Candidate
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => setIsRequisitionModalOpen(true)}
          >
            Create Requisition
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Open Positions
              </Typography>
              <Typography variant="h4" color="primary">
                {requisitions.filter(r => r.status === 'open').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Candidates
              </Typography>
              <Typography variant="h4" color="info.main">
                {candidates.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                In Interview
              </Typography>
              <Typography variant="h4" color="warning.main">
                {pipeline?.interviewing?.length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pending Offers
              </Typography>
              <Typography variant="h4" color="success.main">
                {pipeline?.offer_pending?.length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Active Requisitions */}
      <Typography variant="h6" gutterBottom>
        Active Requisitions
      </Typography>
      <Paper sx={{ mb: 4 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Position</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Hiring Manager</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Applications</TableCell>
                <TableCell>Target Start</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requisitions.map((requisition) => (
                <TableRow key={requisition.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body1">{requisition.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {requisition.level}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{requisition.department?.name}</TableCell>
                  <TableCell>{requisition.hiringManager ? displayName(requisition.hiringManager) : '-'}</TableCell>
                  <TableCell>{requisition.location || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={requisition.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(requisition.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{requisition.applications?.length || 0}</TableCell>
                  <TableCell>
                    {requisition.targetStartDate 
                      ? new Date(requisition.targetStartDate).toLocaleDateString()
                      : '-'
                    }
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<Work />}
                      onClick={() => setSelectedRequisitionId(requisition.id)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {requisitions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      No requisitions found. Create your first requisition to start hiring.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Recent Candidates */}
      <Typography variant="h6" gutterBottom>
        Recent Candidates
      </Typography>
      <Paper sx={{ overflowX: "auto" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Current Role</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Applications</TableCell>
                <TableCell>Added</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {candidates.slice(0, 10).map((candidate) => (
                <TableRow key={candidate.id} hover>
                  <TableCell>{displayName(candidate)}</TableCell>
                  <TableCell>{candidate.email}</TableCell>
                  <TableCell>
                    {candidate.currentTitle && candidate.currentCompany 
                      ? `${candidate.currentTitle} at ${candidate.currentCompany}`
                      : candidate.currentTitle || candidate.currentCompany || '-'
                    }
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={candidate.source?.replace('_', ' ').toUpperCase()}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{candidate.applications?.length || 0}</TableCell>
                  <TableCell>
                    {new Date(candidate.createdAt).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
              {candidates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">
                      No candidates found. Add your first candidate to start building your talent pool.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CreateRequisitionModal
        open={isRequisitionModalOpen}
        onClose={() => setIsRequisitionModalOpen(false)}
        onRequisitionCreated={() => {
          fetchRecruitmentData();
        }}
      />

      <AddCandidateModal
        open={isCandidateModalOpen}
        onClose={() => setIsCandidateModalOpen(false)}
        onCandidateAdded={() => {
          fetchRecruitmentData();
        }}
      />

      <RequisitionDetailModal
        open={!!selectedRequisitionId}
        onClose={() => setSelectedRequisitionId(null)}
        requisitionId={selectedRequisitionId}
        onUpdated={fetchRecruitmentData}
      />
    </Box>
  );
};

export default Recruitment;